import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, ClipboardCheck, Copy, RotateCcw, Send, Users, X } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { api } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { MarkdownLite } from '@/components/ai/markdown-lite'

interface AffectedUser {
  id: string
  display_name: string
  user_principal_name: string
  mail: string | null
  department: string | null
  account_enabled: number
}

interface ThreadMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tool_calls: number
  created_at: string
  /** Users the agent referenced by opaque id, resolved to names by ROI365 inside the tenant. */
  affected_users: AffectedUser[] | null
  affected_total: number | null
}

/** Table of affected users under an answer. Names come from ROI365, never from the agent. */
function AffectedUsersTable({ users, total }: { users: AffectedUser[]; total: number }) {
  const navigate = useNavigate()
  const copyMails = async () => {
    const mails = users.map((u) => u.mail || u.user_principal_name).join('; ')
    try { await navigator.clipboard.writeText(mails); toast.success(`${users.length} correo(s) copiados`) } catch { toast.error('No se pudo copiar al portapapeles') }
  }
  return (
    <div className="mt-2 rounded-md border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        <span className="inline-flex items-center gap-1 font-medium" style={{ color: 'var(--color-text-primary)' }}><Users className="h-3.5 w-3.5" /> Usuarios afectados ({total}{users.length < total ? `, mostrando ${users.length}` : ''})</span>
        <button type="button" className="inline-flex items-center gap-1 underline" style={{ color: 'var(--color-text-link)' }} onClick={copyMails}><Copy className="h-3 w-3" /> Copiar correos</button>
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="px-3 py-1.5">
                  <button type="button" className="text-left hover:underline" style={{ color: 'var(--color-text-link)' }} onClick={() => navigate({ to: '/users', search: { email: u.user_principal_name } })}>
                    <span className="font-medium">{u.display_name}</span>
                    <span className="block" style={{ color: 'var(--color-text-tertiary)' }}>{u.user_principal_name}</span>
                  </button>
                </td>
                <td className="px-3 py-1.5" style={{ color: 'var(--color-text-secondary)' }}>{u.department ?? '—'}</td>
                <td className="px-3 py-1.5" style={{ color: u.account_enabled ? 'var(--color-success-dark)' : 'var(--color-text-tertiary)' }}>{u.account_enabled ? 'Activo' : 'Deshabilitado'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-3 py-1.5 text-[10px]" style={{ color: 'var(--color-text-tertiary)' }}>Nombres resueltos por ROI365 dentro de vuestro tenant; el investigador solo maneja identificadores.</p>
    </div>
  )
}

interface ThreadData {
  thread: { id: string; status: string; turns: number; max_turns: number; error_message: string | null } | null
  messages: ThreadMessage[]
  maxTurns: number
}

const FINDING_QUESTIONS = [
  '¿Qué tendría que comprobar para fiarme de este ahorro?',
  '¿Qué usuarios o licencias quedarían afectados?',
  '¿Qué riesgo hay si lo aplico en la renovación?',
]
const TENANT_QUESTIONS = [
  '¿Qué licencias tienen más unidades sin asignar?',
  '¿Dónde está el mayor gasto con menos uso?',
  '¿Qué vence en los próximos 90 días?',
]

/**
 * "Preguntar al investigador": a side panel with the per-user thread about one
 * finding. Questions are answered asynchronously by the agent (queue); while a
 * turn is open the thread is polled every 5 seconds.
 */
/**
 * `findingId` null = general thread about the tenant's licensing (one active
 * conversation per user, "Nueva conversación" wipes it).
 */
export function FindingChatPanel({ findingId, findingTitle, canWrite, onClose }: { findingId: string | null; findingTitle: string; canWrite: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const base = findingId ? `/ai-recommendations/${findingId}/thread` : '/ai-recommendations/thread'
  const threadKey = ['ai-recommendations', 'thread', findingId ?? 'tenant']
  const suggestions = findingId ? FINDING_QUESTIONS : TENANT_QUESTIONS

  const { data, isLoading, isError } = useQuery({
    queryKey: threadKey,
    queryFn: () => api.get<ThreadData>(base),
    refetchInterval: (q) => (q.state.data?.thread?.status === 'thinking' ? 5_000 : false),
  })
  const thread = data?.thread ?? null
  const messages = data?.messages ?? []
  const thinking = thread?.status === 'thinking'
  const maxTurns = thread?.max_turns ?? data?.maxTurns ?? 10
  const turnsLeft = Math.max(0, maxTurns - (thread?.turns ?? 0))
  const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant')?.content ?? null

  const send = useMutation({
    mutationFn: (question: string) => api.post(`${base}/messages`, { text: question }),
    onSuccess: () => { setText(''); queryClient.invalidateQueries({ queryKey: threadKey }) },
    onError: (err) => toast.error((err as Error).message),
  })
  const reset = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => { toast.success('Conversación reiniciada'); queryClient.invalidateQueries({ queryKey: threadKey }) },
    onError: (err) => toast.error((err as Error).message),
  })
  const saveNote = useMutation({
    mutationFn: (note: string) => api.put(`/ai-recommendations/${findingId}/review`, { note: note.slice(0, 2000) }),
    onSuccess: () => { toast.success('Respuesta guardada como nota de revisión'); queryClient.invalidateQueries({ queryKey: ['ai-recommendations'] }) },
    onError: (err) => toast.error((err as Error).message),
  })

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length, thinking])

  const canAsk = canWrite && !thinking && turnsLeft > 0 && !send.isPending
  const submit = () => { const q = text.trim(); if (q && canAsk) send.mutate(q) }

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label="Preguntar al investigador">
      <div className="flex-1" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }} onClick={onClose} />
      <aside className="flex h-full w-full max-w-md flex-col border-l" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-start justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}><Bot className="h-4 w-4" /> {findingId ? 'Preguntar al investigador' : 'Investigador de licencias'}</p>
            <p className="truncate text-xs" style={{ color: 'var(--color-text-tertiary)' }} title={findingTitle}>{findingTitle}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {!findingId && canWrite && messages.length > 0 && (
              <Button variant="secondary" size="sm" loading={reset.isPending} disabled={thinking} onClick={() => { if (window.confirm('Se borrará esta conversación. ¿Empezar una nueva?')) reset.mutate() }}><RotateCcw className="h-3.5 w-3.5" /> Nueva conversación</Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {isLoading ? (
            <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />)}</div>
          ) : isError ? (
            <p className="text-sm" style={{ color: 'var(--color-error)' }}>No se pudo cargar la conversación.</p>
          ) : messages.length === 0 ? (
            <div className="space-y-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {findingId ? (
                <p>El investigador conoce este hallazgo, los datos de vuestro tenant y las fuentes que consultó. Pregúntale lo que necesites para decidir si el ahorro es útil. No puede modificar nada.</p>
              ) : (
                <>
                  <p>Pregunta sobre el licenciamiento Microsoft 365 de vuestra organización: licencias, unidades, uso, precios contractuales, vencimientos, solapamientos y riesgos. Solo maneja los datos sincronizados y agregados; para un usuario concreto usa la página de usuarios. No puede modificar nada.</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Las señales a investigar y la página de suscripciones ya muestran unidades, uso y vencimientos sin coste de IA.</p>
                </>
              )}
              {canWrite && (
                <div className="space-y-1.5">
                  {suggestions.map((q) => (
                    <button key={q} type="button" className="block w-full rounded-md border px-3 py-2 text-left text-xs hover:underline" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-link)' }} onClick={() => send.mutate(q)} disabled={!canAsk}>{q}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className="max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm"
                  style={m.role === 'user'
                    ? { backgroundColor: 'var(--color-brand-primary)', color: 'white' }
                    : m.role === 'system'
                      ? { backgroundColor: 'var(--color-warning-light)', color: 'var(--color-text-primary)' }
                      : { backgroundColor: 'var(--color-gray-100)', color: 'var(--color-text-primary)' }}
                >
                  {m.role === 'assistant' ? <MarkdownLite text={m.content} /> : m.content}
                  <p className="mt-1 text-[10px] opacity-70">{formatDateTime(m.created_at)}{m.role === 'assistant' && m.tool_calls > 0 ? ` · consultó ${m.tool_calls} dato(s) del tenant` : ''}</p>
                  {m.affected_users && m.affected_users.length > 0 && <AffectedUsersTable users={m.affected_users} total={m.affected_total ?? m.affected_users.length} />}
                  {m.affected_users && m.affected_users.length === 0 && m.affected_total ? <p className="mt-1 text-[10px] opacity-70">Los identificadores citados no corresponden a usuarios de vuestro tenant.</p> : null}
                </div>
              </div>
            ))
          )}
          {thinking && (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              <span className="h-2 w-2 animate-pulse rounded-full" style={{ backgroundColor: 'var(--color-brand-secondary)' }} />
              El investigador está consultando los datos. Suele tardar entre 10 y 60 segundos.
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="space-y-2 border-t p-4" style={{ borderColor: 'var(--color-border)' }}>
          {findingId && lastAnswer && canWrite && (
            <Button variant="secondary" size="sm" onClick={() => saveNote.mutate(lastAnswer)} loading={saveNote.isPending}><ClipboardCheck className="h-3.5 w-3.5" /> Guardar última respuesta como nota de revisión</Button>
          )}
          {canWrite ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
                placeholder={turnsLeft === 0 ? 'Este hilo alcanzó el máximo de preguntas' : thinking ? 'Espera la respuesta anterior…' : 'Escribe tu pregunta (Enter para enviar)'}
                disabled={!canAsk}
                rows={3}
                maxLength={2000}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
              />
              <div className="flex items-center justify-between text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                <span>{turnsLeft} de {maxTurns} preguntas restantes · cuenta en el presupuesto de IA del tenant</span>
                <Button variant="primary" size="sm" onClick={submit} disabled={!canAsk || !text.trim()} loading={send.isPending}><Send className="h-3.5 w-3.5" /> Enviar</Button>
              </div>
            </>
          ) : (
            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Tu rol es de solo lectura: puedes leer la conversación pero no preguntar.</p>
          )}
        </div>
      </aside>
    </div>
  )
}
